import { transformImage } from '../../model/algolia/utils';
import { ArrayType } from '../../types/common';
import {
  ExpectedTierType,
  JackpotProviders,
  JackpotSlotMapped,
  JackpotsV2StateValues,
  JackpotTile,
  JackpotV2ValueInterface,
  MultipleJackpotV2Interface,
} from '../../types/jackpotTypes';

export const getJackpotPropertiesFromJackpotTile = ({
  jackpotTile,
  multiJackpots,
  currency,
  locale,
}: {
  jackpotTile?: JackpotTile;
  multiJackpots?: Record<string, MultipleJackpotV2Interface>;
  currency?: string;
  locale?: string;
}): JackpotSlotMapped | undefined => {
  if (!jackpotTile || !multiJackpots) return;
  const jackpotData = multiJackpots[jackpotTile.code];
  if (!jackpotData || !jackpotData.values) return;

  const mapper =
    jackpotTile.provider === JackpotProviders.BluePrint
      ? blueprintMapper
      : redTigerMapper;
  const tiers = jackpotTile.fields.map(mapper(jackpotData.values));

  const timestamp = new Date(jackpotData.timestamp || Date.now()).getTime();
  return buildJackpotSlots(jackpotTile, tiers, timestamp, currency, locale);
};

const redTigerMapper = (
  jpValuesPerCode: Record<string, JackpotV2ValueInterface>
) => (
  jpField: ArrayType<JackpotTile['fields']>,
  index: number
): ExpectedTierType => {
  const jpValue = jpValuesPerCode[jpField.code];
  if (!jpValue) {
    return {
      name: '',
      curValue: 0,
      state: 'normal',
    };
  }

  return {
    curValue: jpValue.price,
    name: jpField.title || '',
    releaseMessage: jpField.messages?.active,
    previousPrice: jpValue.previousPrice,
    state:
      jpValue.state === JackpotsV2StateValues.WON
        ? 'won'
        : index === 0
        ? 'normal'
        : 'active',
    ...(jpValue.targetTime && {
      releaseTime: new Date(jpValue.targetTime).toString(),
    }),
  };
};

const blueprintMapper = (
  jpValuesPerCode: Record<string, JackpotV2ValueInterface>
) => (jpField: ArrayType<JackpotTile['fields']>): ExpectedTierType => {
  const jpValue = jpValuesPerCode[jpField.code] || { state: '' };
  return {
    name: jpField.title || '',
    curValue: jpValue.price,
    state: (jpValue.state.toLowerCase() || 'active') as
      | 'normal'
      | 'active'
      | 'won',
  };
};

const buildJackpotSlots = (
  jackpotTile: JackpotTile,
  tiers: ExpectedTierType[],
  timestamp: number,
  currency?: string,
  locale?: string
) => {
  const wonTier = tiers.find(tier => tier.state === 'won');
  return {
    type:
      jackpotTile.provider === JackpotProviders.RedTiger
        ? ('red-tiger' as const)
        : ('vegas-millions' as const),
    staticTexts: jackpotTile.staticTexts,
    timestamp,
    images: {
      background: {
        source: getImageSource(
          wonTier ? jackpotTile.background.won : jackpotTile.background.normal
        ),
      },
      logo: {
        source: getImageSource(jackpotTile.logo.source),
        alt: jackpotTile.logo.alt || '',
      },
    },
    currencyCode: currency,
    locale,
    tiers: tiers,
  };
};

const getImageSource = (src: string | undefined) =>
  src ? transformImage(src) : { normal: '' };
